import type { TaskSyncStatus } from "../../domain/app-types.ts";
import type { Todo } from "../../domain/entities.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import type { SyncState } from "../../infrastructure/sync/automerge.ts";

export interface SyncWorkflowCapabilities {
  actorId: string;
  state(): SyncState;
  setState(state: SyncState): void;
  activeRoot(): string | null;
  selectedTaskId(): string | null;
  editing(): boolean;
  tasks(): Todo[];
  setConflicts(conflicts: Map<string, string[]>): void;
  setStatuses(statuses: Map<string, TaskSyncStatus>): void;
  synchronize(actorId: string, onState: (state: SyncState) => void): Promise<void>;
  entities(rootId: string): Promise<StoredEntity[]>;
  outboxIds(): Promise<Set<string>>;
  visible(entity: StoredEntity): boolean;
  conflictingFields(entity: StoredEntity, actorId: string): string[];
  projectionsChanged(current: Todo[], next: Todo[]): boolean;
  shouldRebuild(contentChanged: boolean, editing: boolean): boolean;
  updateStatePresentation(): void;
  updatePresentation(): void;
  render(): Promise<void>;
  refreshNotifications(): Promise<void>;
  loadActivity(taskId: string): Promise<void>;
  refreshOpenTask(taskId: string): Promise<void>;
  reconcileCalendars(): void;
  retryToast(): void;
}

export function taskSyncStatusMap(todos: Todo[], conflicts: Map<string, string[]>, queuedIds: Set<string>, state: SyncState): Map<string, TaskSyncStatus> {
  return new Map(todos.map(todo => {
    const status: TaskSyncStatus = conflicts.get(todo.id)?.length ? "conflicted" : queuedIds.has(todo.id) ? state === "attention" ? "error" : "queued" : state === "synced" ? "synced" : "local";
    return [todo.id, status];
  }));
}

export function createSyncWorkflowController(capabilities: SyncWorkflowCapabilities) {
  let running: Promise<void> | undefined;
  const conflictCache = new Map<string, { updatedAt: number; fields: string[] }>();
  const cachedConflictingFields = (entity: StoredEntity): string[] => {
    if (entity.kind !== "todo") return [];
    const updatedAt = Number((entity.projection as Todo).updatedAt || entity.updatedAt); const cached = conflictCache.get(entity.id);
    if (cached?.updatedAt === updatedAt) return cached.fields;
    const fields = capabilities.conflictingFields(entity, capabilities.actorId); conflictCache.set(entity.id, { updatedAt, fields }); return fields;
  };
  const refreshPresentation = async (): Promise<boolean> => {
    const rootId = capabilities.activeRoot(); if (!rootId) { capabilities.updatePresentation(); return false; }
    const entities = (await capabilities.entities(rootId)).filter(capabilities.visible);
    const nextTodos = entities.filter(entity => entity.kind === "todo").map(entity => entity.projection as Todo);
    if (capabilities.shouldRebuild(capabilities.projectionsChanged(capabilities.tasks(), nextTodos), capabilities.editing())) { await capabilities.render(); return true; }
    const conflicts = new Map(entities.map(entity => [entity.id, cachedConflictingFields(entity)])); capabilities.setConflicts(conflicts);
    capabilities.setStatuses(taskSyncStatusMap(capabilities.tasks(), conflicts, await capabilities.outboxIds(), capabilities.state())); capabilities.updatePresentation(); return false;
  };
  const run = async (): Promise<void> => {
    await capabilities.synchronize(capabilities.actorId, state => { capabilities.setState(state); capabilities.updateStatePresentation(); });
    if (capabilities.state() === "synced") {
      await capabilities.refreshNotifications(); const selectedId = capabilities.selectedTaskId(); if (selectedId) await capabilities.loadActivity(selectedId);
      const shellRebuilt = await refreshPresentation(); if (!shellRebuilt && selectedId) await capabilities.refreshOpenTask(selectedId); capabilities.reconcileCalendars();
    } else if (capabilities.state() === "attention") { await refreshPresentation(); capabilities.retryToast(); }
  };
  return {
    sync(): Promise<void> { if (running) return running; running = run().finally(() => { running = undefined; }); return running; },
    refreshPresentation,
    conflictingFields: cachedConflictingFields,
  };
}
